import { primaryNavigation, solutions, industries } from "@shared/siteContent";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { trackConversion } from "@/lib/analytics";
import { useLocale } from "@/contexts/LocaleContext";
import { localizedPage, ui } from "@shared/i18n";
import { copyFor } from "@shared/bilingualContent";

export const ASSETS = {
  hero: "/manus-storage/qm-hero-1920_4b13f7b9.webp",
  heroMobile: "/manus-storage/qm-hero-960_f1c1009e.webp",
  corridor: "/manus-storage/qm-corridor-1600_d4b1eb0b.webp",
  corridorMobile: "/manus-storage/qm-corridor-800_e019e197.webp",
  terrain: "/manus-storage/qm-terrain-1600_bfa10f21.webp",
  terrainMobile: "/manus-storage/qm-terrain-800_d2351039.webp",
  urban: "/manus-storage/qm-urban-1600_f08e2773.webp",
  urbanMobile: "/manus-storage/qm-urban-800_bf15558e.webp",
  logoMark: "/manus-storage/qm-logo-mark-color-256_2735e023.webp",
  logoLockupLight: "/manus-storage/qm-logo-lockup-light-1400_f4d61d13.webp",
  logoLockupLightMobile: "/manus-storage/qm-logo-lockup-light-700_a3ff8bdb.webp",
  logoLockupColor: "/manus-storage/qm-logo-lockup-color-1400_dbcd99dd.webp",
} as const;

export function BrandMark({ compact = false }: { compact?: boolean }) {
  const { locale } = useLocale();
  const copy = copyFor("shell",locale);
  return (
    <span className="inline-flex items-center gap-3" aria-label="Quantum Maps home">
      <img src={ASSETS.logoMark} alt="" width="44" height="44" className="size-11 object-contain" aria-hidden="true" />
      {!compact && (
        <span className="flex flex-col leading-none">
          <span className="brand-wordmark text-[1rem] font-semibold tracking-[0.02em] text-white">Quantum Maps</span>
          <span className="mt-1 text-[0.5rem] font-semibold uppercase tracking-[0.18em] text-indigo-200/80">{copy.tagline}</span>
        </span>
      )}
    </span>
  );
}

export function BrandLockup() {
  return <img src={ASSETS.logoLockupLight} srcSet={`${ASSETS.logoLockupLightMobile} 700w, ${ASSETS.logoLockupLight} 1400w`} sizes="(max-width: 640px) 260px, 340px" width="340" height="74" alt="Quantum Maps — Mapping Futures with Precision" className="h-auto w-[min(21rem,80vw)]" />;
}

function DesktopMegaMenu({ type }: { type: "solutions" | "industries" }) {
  const { locale } = useLocale();
  const t = ui[locale];
  const pages = (type === "solutions" ? solutions : industries).map((page)=>localizedPage(page,locale));
  return (
    <div className="group relative">
      <button className="flex items-center gap-1.5 py-4 text-sm font-medium text-slate-200 hover:text-cyan-200" aria-haspopup="true">
        {type === "solutions" ? t.solutions : t.industries}
        <ChevronDown className="size-3.5 transition-transform group-hover:rotate-180" aria-hidden="true" />
      </button>
      <div className="invisible absolute left-0 top-[calc(100%-0.25rem)] z-50 w-[38rem] translate-y-2 border border-cyan-100/15 bg-[#071014]/95 p-3 opacity-0 shadow-2xl backdrop-blur-xl transition group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
        <div className="grid grid-cols-2 gap-px bg-white/5">
          {pages.map((page) => (
            <Link key={page.path} href={page.path} className="group/item bg-[#071014] p-4 hover:bg-cyan-200/[0.055]">
              <span className="block text-sm font-semibold text-slate-100 group-hover/item:text-cyan-200">{page.navLabel}</span>
              <span className="mt-1.5 block text-xs leading-5 text-slate-400">{page.summary}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SiteHeader() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const { locale, switchLocale } = useLocale();
  const t = ui[locale];
  const navLabels: Record<string,string> = {"/projects":t.projects,"/technology":t.technology,"/insights":t.insights,"/about":t.about};

  useEffect(() => setOpen(false), [location]);
  useEffect(() => {
    if (!open) return;
    const onEscape = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [open]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#05080a]/82 backdrop-blur-xl">
      <div className="content-width flex h-20 items-center justify-between gap-8">
        <Link href="/" className="shrink-0"><BrandMark /></Link>
        <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary navigation">
          <DesktopMegaMenu type="solutions" />
          <DesktopMegaMenu type="industries" />
          {primaryNavigation.slice(2).map((item) => (
            <Link key={item.href} href={item.href} className={`py-4 text-sm font-medium ${location === item.href ? "text-cyan-200" : "text-slate-200 hover:text-cyan-200"}`}>
              {navLabels[item.href] || item.label}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-4 lg:flex">
          <button type="button" onClick={switchLocale} className="min-h-11 px-2 font-mono text-xs font-semibold text-indigo-100 hover:text-white" aria-label={t.switchLanguage}>{t.language}</button>
          <Link href="/contact" className="text-sm font-medium text-slate-300 hover:text-cyan-200">{t.contact}</Link>
          <Link href="/contact" className="button-primary !min-h-11 !px-4" onClick={() => trackConversion("cta_start_project", { placement: "header" })}>
            {t.start} <ArrowUpRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
        <div className="flex items-center gap-2 lg:hidden"><button type="button" onClick={switchLocale} className="min-h-11 px-2 font-mono text-xs font-semibold text-indigo-100" aria-label={t.switchLanguage}>{t.language}</button><button className="grid size-11 place-items-center border border-white/20 bg-black/25 text-white" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? t.menuClose : t.menuOpen}>
          {open ? <X className="size-5" /> : <Menu className="size-5" />}
        </button></div>
      </div>
      {open && (
        <nav id="mobile-menu" className="h-[calc(100dvh-5rem)] overflow-y-auto border-t border-white/10 bg-[#05080a] lg:hidden" aria-label="Mobile navigation">
          <div className="content-width py-7">
            <p className="mono-label mb-3">{t.solutions}</p>
            <div className="grid gap-px border-y border-white/10">
              {solutions.map((page) => {const item=localizedPage(page,locale);return <Link key={page.path} href={page.path} className="flex items-center justify-between border-b border-white/10 py-3.5 text-lg text-white last:border-0">{item.navLabel}<ArrowUpRight className="size-4 text-cyan-200" /></Link>;})}
            </div>
            <p className="mono-label mb-3 mt-8">{t.industries}</p>
            <div className="grid grid-cols-2 gap-x-5 gap-y-3">
              {industries.map((page) => {const item=localizedPage(page,locale);return <Link key={page.path} href={page.path} className="text-sm text-slate-300 hover:text-cyan-200">{item.navLabel}</Link>;})}
            </div>
            <div className="mt-8 grid gap-px border-y border-white/10">
              {primaryNavigation.slice(2).map((item) => <Link key={item.href} href={item.href} className="flex items-center justify-between border-b border-white/10 py-3.5 text-lg text-white last:border-0">{navLabels[item.href] || item.label}<ArrowUpRight className="size-4 text-cyan-200" /></Link>)}
            </div>
            <Link href="/contact" className="button-primary mt-8 w-full" onClick={() => trackConversion("cta_start_project", { placement: "mobile_menu" })}>{t.start} <ArrowUpRight className="size-4" /></Link>
          </div>
        </nav>
      )}
    </header>
  );
}

export function SiteFooter() {
  const { locale } = useLocale();
  const t = ui[locale];
  const c = copyFor("shell",locale);
  return (
    <footer className="border-t border-white/10 bg-[#040607]">
      <div className="content-width grid gap-12 py-16 lg:grid-cols-[1.25fr_2fr] lg:py-20">
        <div>
          <BrandLockup />
          <p className="mt-6 max-w-sm text-xl leading-8 text-slate-200">{c.footerLead}</p>
          <p className="mt-6 max-w-md text-sm leading-6 text-slate-500">{c.footerBody}</p>
        </div>
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
          <div><p className="mono-label mb-4">{t.company}</p><div className="grid gap-3 text-sm text-slate-300"><Link href="/about">{t.about}</Link><Link href="/projects">{t.projects}</Link><Link href="/technology">{t.technology}</Link><Link href="/beyond-todays-maps">{c.rd}</Link></div></div>
          <div><p className="mono-label mb-4">{t.knowledge}</p><div className="grid gap-3 text-sm text-slate-300"><Link href="/insights">{t.insights}</Link><Link href="/lidar-3d-reality-capture">{locale==="ar"?"الليدار والالتقاط ثلاثي الأبعاد":"LiDAR & 3D Reality Capture"}</Link><Link href="/navigation-sensor-fusion">{locale==="ar"?"الملاحة ودمج المستشعرات":"Navigation & Sensor Fusion"}</Link><Link href="/satellite-remote-sensing-intelligence">{locale==="ar"?"الاستشعار عن بُعد":"Satellite & Remote Sensing"}</Link></div></div>
          <div><p className="mono-label mb-4">{t.locations}</p><div className="grid gap-3 text-sm text-slate-300"><Link href="/locations/canada">{c.canada}</Link><Link href="/locations/toronto">{c.toronto}</Link><Link href="/locations/jordan">{c.jordan}</Link></div></div>
          <div><p className="mono-label mb-4">{t.contact}</p><div className="grid gap-3 text-sm text-slate-300"><Link href="/contact">{t.start}</Link><span className="text-slate-500">{c.contactPending}</span></div></div>
        </div>
      </div>
      <div className="content-width flex flex-col gap-4 border-t border-white/10 py-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <p>© {new Date().getFullYear()} Quantum Maps. {c.rights}</p>
        <div className="flex gap-5"><Link href="/privacy">{c.privacy}</Link><Link href="/terms">{c.terms}</Link><Link href="/cookies">{c.cookies}</Link></div>
      </div>
    </footer>
  );
}

export function SiteShell({ children }: { children: React.ReactNode }) {
  const { locale } = useLocale();
  return (
    <div className="site-shell">
      <a href="#main-content" className="skip-link">{ui[locale].skip}</a>
      <SiteHeader />
      <main id="main-content">{children}</main>
      <SiteFooter />
    </div>
  );
}

export function Breadcrumbs({ items }: { items: Array<{ label: string; href?: string }> }) {
  const { locale } = useLocale();
  return (
    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 font-mono text-[0.67rem] uppercase tracking-[0.1em] text-slate-500">
      <Link href="/" className="hover:text-cyan-200">{ui[locale].home}</Link>
      {items.map((item) => <span key={item.label} className="flex items-center gap-2"><span aria-hidden="true">/</span>{item.href ? <Link href={item.href} className="hover:text-cyan-200">{item.label}</Link> : <span aria-current="page" className="text-slate-300">{item.label}</span>}</span>)}
    </nav>
  );
}

export function FinalCta() {
  const { locale } = useLocale();
  const t = ui[locale];
  const c = copyFor("shell",locale);
  return (
    <section className="relative overflow-hidden border-y border-cyan-100/15 bg-[#071014] py-24 sm:py-32">
      <div className="technical-grid absolute inset-0 opacity-25" aria-hidden="true" />
      <div className="point-field absolute inset-0 opacity-25" aria-hidden="true" />
      <div className="content-width relative grid items-end gap-10 lg:grid-cols-[1.4fr_0.6fr]">
        <div><p className="eyebrow">{c.startEnvironment}</p><h2 className="display-lg mt-6 max-w-5xl">{c.complexEnvironment}</h2><p className="lede mt-7">{c.complexBody}</p></div>
        <div className="flex flex-col gap-3 lg:items-end"><Link href="/contact" className="button-primary w-full sm:w-auto" onClick={() => trackConversion("cta_start_project", { placement: "final_cta" })}>{t.start} <ArrowUpRight className="size-4" /></Link><Link href="/contact" className="button-secondary w-full sm:w-auto" onClick={() => trackConversion("cta_talk_to_engineer", { placement: "final_cta" })}>{c.talk}</Link></div>
      </div>
    </section>
  );
}
